/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  faUser, faEnvelope, faLock, faUserShield, faEye, faEyeSlash, faArrowLeft,
  faBuilding, faMapMarkerAlt, faGlobe, faPlus, faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import '../../../assets/css/User/AddUser.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

function AddUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const userToEdit = location.state?.user;
  const fileInputRef = useRef(null);

  const roleString = localStorage.getItem('role');
  const userRole = roleString ? JSON.parse(roleString).name : null;

  const initialFormData = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    businessName: '',
    tradeName: '',
    logo: '',
    addresses: [{
      address1: '',
      address2: '',
      pincode: '',
      country: '',
      state: '',
      city: '',
      phoneNumber1: '',
      phoneNumber2: '',
      websiteName: '',
      contactPersonName: '',
      faxNumber: '',
      contactPersonDesignation: '',
      isPrimary: true
    }]
  };

  const [formData, setFormData] = useState(initialFormData);
  const [logoPreview, setLogoPreview] = useState(null);
  const [roles, setRoles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [modal, setModal] = useState({ show: false, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [apiErrors, setApiErrors] = useState(null);

  // Initialize form data when userToEdit changes
  useEffect(() => {
    if (userToEdit) {
      const initialData = {
        name: userToEdit.name || '',
        email: userToEdit.email || '',
        password: '',
        confirmPassword: '',
        role: userToEdit.role?._id || '',
        businessName: userToEdit.businessInfo?.businessName || '',
        tradeName: userToEdit.businessInfo?.tradeName || '',
        logo: userToEdit.businessInfo?.logo || '',
        addresses: []
      };

      if (userToEdit.address && userToEdit.address.length > 0) {
        initialData.addresses = userToEdit.address.map(addr => ({
          _id: addr._id,
          address1: addr.address1 || '',
          address2: addr.address2 || '',
          pincode: addr.pincode || '',
          country: addr.country?._id || addr.country || '',
          state: addr.state?._id || addr.state || '',
          city: addr.city?._id || addr.city || '',
          phoneNumber1: addr.phoneNumber1 || '',
          phoneNumber2: addr.phoneNumber2 || '',
          websiteName: addr.websiteName || '',
          contactPersonName: addr.contactPersonName || '',
          faxNumber: addr.faxNumber || '',
          contactPersonDesignation: addr.contactPersonDesignation || '',
          isPrimary: addr.isPrimary || false
        }));
      }

      setFormData(initialData);

      if (userToEdit.businessInfo?.logo) {
        setLogoPreview(`${API_BASE_URL}/uploads/${userToEdit.businessInfo.logo}`);
      }
    }
  }, [userToEdit]);

  const getAxiosConfig = useCallback(() => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      navigate('/login');
      return {};
    }
    return {
      headers: {
        Authorization: `Bearer ${storedToken}`,
        'Content-Type': 'application/json',
      }
    };
  }, [navigate]);

  const getMultipartConfig = useCallback(() => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      navigate('/login');
      return {};
    }
    return {
      headers: {
        Authorization: `Bearer ${storedToken}`,
        'Content-Type': 'multipart/form-data',
      }
    };
  }, [navigate]);

  const showModal = useCallback((message) => {
    setModal({ show: true, message });
    setTimeout(() => {
      setModal({ show: false, message: '' });
      if (message.includes('✅')) navigate('/users');
    }, 3000);
  }, [navigate]);

  const fetchInitialData = useCallback(async () => {
    try {
      const config = getAxiosConfig();
      if (!config.headers) return;

      const [rolesRes, countriesRes, statesRes, citiesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/roles`, config),
        axios.get(`${API_BASE_URL}/api/countries`, config),
        axios.get(`${API_BASE_URL}/api/states`, config),
        axios.get(`${API_BASE_URL}/api/cities`, config),
      ]);

      setRoles(rolesRes.data);
      setCountries(countriesRes.data);
      setAllStates(statesRes.data);
      setAllCities(citiesRes.data);
      setIsLoading(false);

    } catch (error) {
      console.error('Error fetching initial data:', error);
      showModal('Error fetching data, please try again.');
      setIsLoading(false);
    }
  }, [getAxiosConfig, showModal]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const filteredRoles = useMemo(() => {
    if (userRole === 'Admin') return roles;
    if (userRole === 'Seller') return roles.filter(role => role.name === 'Buyer');
    return [];
  }, [roles, userRole]);

  const getFilteredStates = useCallback((countryId) => {
    if (!countryId) return [];
    return allStates.filter(
      state => state.country === countryId || state.country?._id === countryId
    );
  }, [allStates]);

  const getFilteredCities = useCallback((stateId) => {
    if (!stateId) return [];
    return allCities.filter(
      city => city.state === stateId || city.state?._id === stateId
    );
  }, [allCities]);

  const handleCountryChange = (index, countryId) => {
    setFormData(prev => {
      const updatedAddresses = [...prev.addresses];
      updatedAddresses[index] = {
        ...updatedAddresses[index],
        country: countryId,
        state: '',
        city: ''
      };
      return { ...prev, addresses: updatedAddresses };
    });
  };

  const handleStateChange = (index, stateId) => {
    setFormData(prev => {
      const updatedAddresses = [...prev.addresses];
      updatedAddresses[index] = {
        ...updatedAddresses[index],
        state: stateId,
        city: ''
      };
      return { ...prev, addresses: updatedAddresses };
    });
  };

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => {
      const updatedAddresses = [...prev.addresses];
      updatedAddresses[index] = {
        ...updatedAddresses[index],
        [field]: value
      };
      return { ...prev, addresses: updatedAddresses };
    });
  };

  const handlePrimaryAddressChange = (index) => {
    setFormData(prev => {
      const updatedAddresses = prev.addresses.map((address, i) => ({
        ...address,
        isPrimary: i === index
      }));
      return { ...prev, addresses: updatedAddresses };
    });
  };

  const addNewAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [
        ...prev.addresses,
        {
          address1: '',
          address2: '',
          pincode: '',
          country: '',
          state: '',
          city: '',
          phoneNumber1: '',
          phoneNumber2: '',
          websiteName: '',
          contactPersonName: '',
          faxNumber: '',
          contactPersonDesignation: '',
          isPrimary: false
        }
      ]
    }));
  };

  const removeAddress = (index) => {
    if (formData.addresses.length <= 1) return;
    
    if (!window.confirm('Are you sure you want to remove this address?')) {
      return;
    }
    
    setFormData(prev => {
      const updatedAddresses = [...prev.addresses];
      const removedAddress = updatedAddresses.splice(index, 1)[0];
      
      if (removedAddress.isPrimary && updatedAddresses.length > 0) {
        updatedAddresses[0].isPrimary = true;
      }
      
      return { ...prev, addresses: updatedAddresses };
    });
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'logo') {
      const file = files[0];
      if (file && file.size > 2 * 1024 * 1024) {
        setFieldErrors(prev => ({ ...prev, logo: 'File size should be less than 2MB' }));
        return;
      }

      setFormData(prev => ({ ...prev, logo: file }));

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setLogoPreview(userToEdit?.businessInfo?.logo
          ? `${API_BASE_URL}/uploads/${userToEdit.businessInfo.logo}`
          : null
        );
      }
    } else if (name === 'role') {
      setFormData(prev => ({
        ...prev,
        role: value,
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (password) => password.length >= 6; // Basic validation

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!validateEmail(formData.email)) errors.email = 'Invalid email format';
    
    if (!userToEdit) {
      if (!formData.password) errors.password = 'Password is required';
      else if (!validatePassword(formData.password)) {
        errors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    } else if (formData.password && !validatePassword(formData.password)) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!formData.role) errors.role = 'Role is required';
    if (!formData.businessName.trim()) errors.businessName = 'Business name is required';
    if (!formData.tradeName.trim()) errors.tradeName = 'Trade name is required';

    formData.addresses.forEach((address, index) => {
      if (!address.address1.trim()) errors[`address1_${index}`] = 'Address line 1 is required';
      if (!address.pincode.trim()) errors[`pincode_${index}`] = 'Pincode is required';
      if (!address.country) errors[`country_${index}`] = 'Country is required';
      if (!address.state) errors[`state_${index}`] = 'State is required';
      if (!address.city) errors[`city_${index}`] = 'City is required';
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBusinessInfo = async (multipartConfig) => {
    const businessInfoFormData = new FormData();
    businessInfoFormData.append('businessName', formData.businessName);
    businessInfoFormData.append('tradeName', formData.tradeName);

    if (formData.logo instanceof File) {
      businessInfoFormData.append('logo', formData.logo);
    } else if (formData.logo && typeof formData.logo === 'string') {
      businessInfoFormData.append('logo', formData.logo);
    }

    if (userToEdit?.businessInfo?._id) {
      const response = await axios.put(
        `${API_BASE_URL}/api/businessinfo/${userToEdit.businessInfo._id}`,
        businessInfoFormData,
        multipartConfig
      );
      return response.data._id;
    } else {
      const response = await axios.post(
        `${API_BASE_URL}/api/businessinfo`,
        businessInfoFormData,
        multipartConfig
      );
      return response.data._id;
    }
  };

  const handleAddresses = async (config) => {
    const addressPromises = formData.addresses.map(async (address) => {
      const addressData = {
        address1: address.address1,
        address2: address.address2,
        pincode: address.pincode,
        country: address.country,
        state: address.state,
        city: address.city,
        phoneNumber1: address.phoneNumber1,
        phoneNumber2: address.phoneNumber2,
        websiteName: address.websiteName,
        contactPersonName: address.contactPersonName,
        faxNumber: address.faxNumber,
        contactPersonDesignation: address.contactPersonDesignation,
        isPrimary: address.isPrimary
      };

      if (address._id) {
        const response = await axios.put(
          `${API_BASE_URL}/api/address/${address._id}`,
          addressData,
          config
        );
        return response.data._id;
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/address`,
          addressData,
          config
        );
        return response.data._id;
      }
    });

    const addressResponses = await Promise.all(addressPromises);
    return addressResponses;
  };

  const handleUser = async (config, businessInfoId, addressIds) => {
    const userPayload = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      businessInfo: businessInfoId,
      address: addressIds,
    };

    if (formData.password) {
      userPayload.password = formData.password;
    }

    const userUrl = userToEdit
      ? `${API_BASE_URL}/api/users/${userToEdit._id}`
      : `${API_BASE_URL}/api/users/add`;

    const userMethod = userToEdit ? 'put' : 'post';

    const response = await axios[userMethod](userUrl, userPayload, config);
    return response.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFieldErrors({});
    setApiErrors(null);

    if (!validateForm()) {
      setIsSubmitting(false);
      showModal('❌ Please fix the form errors');
      return;
    }

    try {
      const config = getAxiosConfig();
      const multipartConfig = getMultipartConfig();
      if (!config.headers || !multipartConfig.headers) return;

      // Handle Business Info
      const businessInfoId = await handleBusinessInfo(multipartConfig);
      
      // Handle Addresses
      const addressIds = await handleAddresses(config);
      
      // Handle User
      await handleUser(config, businessInfoId, addressIds);

      showModal(userToEdit ? '✅ User updated successfully!' : '✅ User added successfully!');
    } catch (error) {
      console.error('Error saving user:', error);
      
      let errorMessage = 'Error saving user. Please try again.';
      
      if (error.response) {
        console.error('Error response data:', error.response.data);
        if (error.response.status === 400 && error.response.data.errors) {
          const serverErrors = {};
          error.response.data.errors.forEach(err => {
            serverErrors[err.path] = err.msg;
          });
          setFieldErrors(serverErrors);
          setApiErrors(error.response.data.errors);
          errorMessage = '❌ Please fix the form errors';
        } else if (error.response.data.message) {
          errorMessage = `❌ ${error.response.data.message}`;
        }
      } else if (error.request) {
        errorMessage = '❌ No response from server. Please check your connection.';
      } else {
        errorMessage = `❌ ${error.message}`;
      }
      
      showModal(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldError = (fieldName, index = null) => {
    const key = index !== null ? `${fieldName}_${index}` : fieldName;
    return fieldErrors[key] ? (
      <span className="field-error">{fieldErrors[key]}</span>
    ) : null;
  };

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="add-user-container">
      <div className="header-bar" style={{borderRadius:'10px'}}>
        <h2 style={{padding:'10px'}}>
          {userToEdit ? 'Edit User' : 'Add New User'}
        </h2>
        <button 
          className="btn-back" 
          onClick={() => navigate('/users')}
          aria-label="Back to users list"
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
      </div>

      <div className="add-user-card">
        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faUser} /> Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter full name"
                  className={fieldErrors.name ? 'error' : ''}
                  aria-invalid={!!fieldErrors.name}
                  aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                />
                {getFieldError('name')}
                {apiErrors?.find(e => e.path === 'name') && (
                  <span className="field-error">{apiErrors.find(e => e.path === 'name').msg}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faEnvelope} /> Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter email address"
                  className={fieldErrors.email ? 'error' : ''}
                  aria-invalid={!!fieldErrors.email}
                />
                {getFieldError('email')}
                {apiErrors?.find(e => e.path === 'email') && (
                  <span className="field-error">{apiErrors.find(e => e.path === 'email').msg}</span>
                )}
              </div>

              <div className="form-group password-group">
                <label>
                  <FontAwesomeIcon icon={faLock} /> Password {!userToEdit && '*'}
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!userToEdit}
                    placeholder={userToEdit ? "Leave blank to keep current" : "Enter password"}
                    className={fieldErrors.password ? 'error' : ''}
                    aria-invalid={!!fieldErrors.password}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="show-hide-btn"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {getFieldError('password')}
                {apiErrors?.find(e => e.path === 'password') && (
                  <span className="field-error">{apiErrors.find(e => e.path === 'password').msg}</span>
                )}
              </div>

              <div className="form-group password-group">
                <label>
                  <FontAwesomeIcon icon={faLock} /> Confirm Password {!userToEdit && '*'}
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required={!userToEdit}
                    placeholder={userToEdit ? "Leave blank to keep current" : "Confirm password"}
                    className={fieldErrors.confirmPassword ? 'error' : ''}
                    aria-invalid={!!fieldErrors.confirmPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="show-hide-btn"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {getFieldError('confirmPassword')}
              </div>

              <div className="form-group">
                <label>
                  <FontAwesomeIcon icon={faUserShield} /> Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className={fieldErrors.role ? 'error' : ''}
                  aria-invalid={!!fieldErrors.role}
                >
                  <option value="">Select Role</option>
                  {filteredRoles.map(role => (
                    <option key={role._id} value={role._id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {getFieldError('role')}
                {apiErrors?.find(e => e.path === 'role') && (
                  <span className="field-error">{apiErrors.find(e => e.path === 'role').msg}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faBuilding} /> Business Information
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Business Name *</label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  placeholder="Business name"
                  className={fieldErrors.businessName ? 'error' : ''}
                />
                {getFieldError('businessName')}
              </div>

              <div className="form-group">
                <label>Trade Name *</label>
                <input
                  type="text"
                  name="tradeName"
                  value={formData.tradeName}
                  onChange={handleChange}
                  required
                  placeholder="Trade name"
                  className={fieldErrors.tradeName ? 'error' : ''}
                />
                {getFieldError('tradeName')}
              </div>

              <div className="form-group">
                <label>Logo (Max 2MB)</label>
                <div className="file-upload-wrapper">
                  <label className="file-upload-label">
                    <input
                      type="file"
                      name="logo"
                      accept="image/*"
                      onChange={handleChange}
                      className="file-upload-input"
                      ref={fileInputRef}
                    />
                    <span className="file-upload-custom">
                      {formData.logo instanceof File
                        ? formData.logo.name
                        : formData.logo
                          ? 'Logo uploaded'
                          : 'Choose file...'}
                    </span>
                  </label>
                </div>
                {getFieldError('logo')}

                {logoPreview && (
                  <div className="logo-preview-wrapper">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="logo-preview-img"
                    />
                    <button
                      className="logo-preview-close"
                      onClick={() => {
                        setLogoPreview(null);
                        setFormData(prev => ({ ...prev, logo: null }));
                        if (fileInputRef.current) {
                          fileInputRef.current.value = null;
                        }
                      }}
                      title="Remove image"
                      aria-label="Remove logo"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Address Information
            </h3>
            
            {formData.addresses.map((address, index) => {
              const filteredStates = getFilteredStates(address.country);
              const filteredCities = getFilteredCities(address.state);

              return (
                <div key={index} className={`address-block ${address.isPrimary ? 'primary-address' : ''}`}>
                  <div className="address-header">
                    <h4>Address {index + 1}</h4>
                    <div className="address-actions">
                      {/* <label className={`primary-address-toggle ${address.isPrimary ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="primaryAddress"
                          checked={address.isPrimary}
                          onChange={() => handlePrimaryAddressChange(index)}
                          aria-label={`Set address ${index + 1} as primary`}
                        />
                        <span className="toggle-label">
                          {address.isPrimary ? 'Primary Address' : 'Set as Primary'}
                        </span>
                      </label> */}
                      {formData.addresses.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove-address"
                          onClick={() => removeAddress(index)}
                          aria-label={`Remove address ${index + 1}`}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Address Line 1 *</label>
                      <input
                        type="text"
                        name="address1"
                        value={address.address1}
                        onChange={(e) => handleAddressChange(index, 'address1', e.target.value)}
                        required
                        placeholder="Address line 1"
                        className={fieldErrors[`address1_${index}`] ? 'error' : ''}
                      />
                      {getFieldError('address1', index)}
                    </div>

                    <div className="form-group">
                      <label>Address Line 2</label>
                      <input
                        type="text"
                        name="address2"
                        value={address.address2}
                        onChange={(e) => handleAddressChange(index, 'address2', e.target.value)}
                        placeholder="Address line 2"
                      />
                    </div>

                    <div className="form-group">
                      <label>Pincode *</label>
                      <input
                        type="text"
                        name="pincode"
                        value={address.pincode}
                        onChange={(e) => handleAddressChange(index, 'pincode', e.target.value)}
                        required
                        placeholder="Pincode"
                        className={fieldErrors[`pincode_${index}`] ? 'error' : ''}
                      />
                      {getFieldError('pincode', index)}
                    </div>

                    <div className="form-group">
                      <label>Phone Number 1</label>
                      <input
                        type="text"
                        name="phoneNumber1"
                        value={address.phoneNumber1}
                        onChange={(e) => handleAddressChange(index, 'phoneNumber1', e.target.value)}
                        placeholder="Phone number"
                      />
                    </div>

                    <div className="form-group">
                      <label>Phone Number 2</label>
                      <input
                        type="text"
                        name="phoneNumber2"
                        value={address.phoneNumber2}
                        onChange={(e) => handleAddressChange(index, 'phoneNumber2', e.target.value)}
                        placeholder="Alternate phone number"
                      />
                    </div>

                    <div className="form-group">
                      <label>Website</label>
                      <input
                        type="text"
                        name="websiteName"
                        value={address.websiteName}
                        onChange={(e) => handleAddressChange(index, 'websiteName', e.target.value)}
                        placeholder="Website URL"
                      />
                    </div>

                    <div className="form-group">
                      <label>Contact Person</label>
                      <input
                        type="text"
                        name="contactPersonName"
                        value={address.contactPersonName}
                        onChange={(e) => handleAddressChange(index, 'contactPersonName', e.target.value)}
                        placeholder="Contact person name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Contact Designation</label>
                      <input
                        type="text"
                        name="contactPersonDesignation"
                        value={address.contactPersonDesignation}
                        onChange={(e) => handleAddressChange(index, 'contactPersonDesignation', e.target.value)}
                        placeholder="Contact person designation"
                      />
                    </div>

                    <div className="form-group">
                      <label>Fax Number</label>
                      <input
                        type="text"
                        name="faxNumber"
                        value={address.faxNumber}
                        onChange={(e) => handleAddressChange(index, 'faxNumber', e.target.value)}
                        placeholder="Fax number"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <FontAwesomeIcon icon={faGlobe} /> Country *
                      </label>
                      <select
                        name="country"
                        value={address.country}
                        onChange={(e) => handleCountryChange(index, e.target.value)}
                        required
                        className={fieldErrors[`country_${index}`] ? 'error' : ''}
                      >
                        <option value="">-- Select Country --</option>
                        {countries.map((country) => (
                          <option key={country._id} value={country._id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      {getFieldError('country', index)}
                    </div>

                    <div className="form-group">
                      <label>State *</label>
                      <select
                        name="state"
                        value={address.state}
                        onChange={(e) => handleStateChange(index, e.target.value)}
                        required
                        disabled={!address.country}
                        className={fieldErrors[`state_${index}`] ? 'error' : ''}
                      >
                        <option value="">-- Select State --</option>
                        {filteredStates.map((state) => (
                          <option key={state._id} value={state._id}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                      {getFieldError('state', index)}
                    </div>

                    <div className="form-group">
                      <label>City *</label>
                      <select
                        name="city"
                        value={address.city}
                        onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                        required
                        disabled={!address.state}
                        className={fieldErrors[`city_${index}`] ? 'error' : ''}
                      >
                        <option value="">-- Select City --</option>
                        {filteredCities.map((city) => (
                          <option key={city._id} value={city._id}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                      {getFieldError('city', index)}
                    </div>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              className="btn-add-address"
              onClick={addNewAddress}
              aria-label="Add another address"
            >
              <FontAwesomeIcon icon={faPlus} /> Add Another Address
            </button>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/users')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Processing...
                </>
              ) : userToEdit ? 'Update User' : 'Add User'}
            </button>
          </div>
        </form>
      </div>

      {modal.show && (
        <div className={`modal-message ${modal.message.includes('✅') ? 'success' : 'error'}`}>
          {modal.message}
        </div>
      )}
    </div>
  );
}

AddUser.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      user: PropTypes.object
    })
  })
};

export default AddUser;